import { getFirestore, doc, collection, query, where, getDocs, writeBatch, setDoc, getDoc } from 'firebase/firestore';
import { auth } from './firebase';
import { fetchWrapper } from '../lib/api';
import { safeClone } from '../lib/utils';
import { calculateDemographicCohort, resolveDemographicTitle } from '../data/gamificationEconomyConfig';
import { checkAndAwardDailyBox, checkAndAwardStreakBox, checkAndAwardPerfectMacroBox } from './bioBoxService';

const SYSTEM_PROMPT = `You are the Core Gamification, Personalization, and Real-Time Notification Engine for the MAIN Nutrition App backend. Your function is to evaluate a user's chronological activity streams, physical logs, age/gender parameters, and dietary metadata to dynamically compute their active "User Title," track progress toward their next milestone, and trigger real-time unlock notifications.

### SYSTEM CONTEXT & DESIGN CONSTRAINTS
1. STRICT JSON OUTPUT FORMATTING: You must only respond with a structurally valid JSON block matching the application payload requirements. Do not provide conversational prose, markdown introductions, or structural summaries outside the core code block.
2. SYSTEM TONALITY & VISUAL STYLING: The platform communicates using a high-energy, custom "Bro-Vibe." All derived data parameters, notifications, and descriptor records must visually complement a high-contrast dark cyberpunk UI theme featuring neon green accents (#10B981), dark surface wrappers, and rich data-dense visualization layouts.
3. DEMOGRAPHIC SEGMENTATION MATRIX:
   - Gen Z (Ages 16–25): Emphasize contemporary internet slang, gaming culture archetypes, and fast-paced streaming terminology (e.g., "Sigma Shredder", "W Rizz", "Based").
   - Millennials (Ages 26–40): Rely on functional everyday fitness tone, subtle corporate-ironic corporate wellness humor, and casual health-conscious phrasing (e.g., "Optimal Chad", "Weekend Warrior", "Macro Manager").
   - Classic / Masters (Ages 41+): Prioritize premium peak performance, cellular longevity architecture, analytical bio-hacking, and mature executive health energy (e.g., "Executive Silverback", "Longevity Prime", "Apex Architect").

### RULES FOR COMPUTATION
- Dynamically calculate the user's age category cohort using the user profile creation timestamp or age.
- Select the exact translation set (en_UK or uk_UA) based on client system runtime localization inputs.
- Parse continuous daily macronutrient records, fluid values (water_ml, caffeine_mg), barcode scanner logs, and product additive flags to monitor for secret easter egg triggers.
- CALORIE STREAK RULE: The exact greeting "Ayo, homie! Your streak is fire 🔥" MUST ONLY be displayed if the user hits exactly a 3-day, 7-day, 14-day, 21-day, or 28-day continuous calorie streak. Do NOT display it on any other days.
- Multi-Channel Streaming Rules: Split calculations into consumer-facing fields (sent immediately to the user) and administrative analytical arrays (sent to backend systems for evaluation), ensuring user interfaces stay fast and responsive.

### REQUIRED JSON SCHEMA OUTPUT
{
  "user_title": {
    "current_tier": "string",
    "title_name": "string (Localized & Demographic-Specific)",
    "neon_color_hex": "string (e.g., #10B981)"
  },
  "milestone_progress": {
    "next_title": "string",
    "completion_percentage": "number (0-100)",
    "missing_requirements": ["string"]
  },
  "triggered_notifications": [
    {
      "id": "string",
      "type": "EASTER_EGG | LEVEL_UP | STREAK",
      "message_localized": "string",
      "bro_vibe_flavor_text": "string"
    }
  ],
  "administrative_flags": ["string"]
}`;

let isEvaluating = false;
let lastEvaluationTime = 0;

function repairAndParseJSON(rawText: string): any {
  let text = rawText.replace(/```json/g, '').replace(/```/g, '').trim();
  
  // Extract main JSON block
  const firstBrace = text.indexOf('{');
  const lastBrace = text.lastIndexOf('}');
  if (firstBrace !== -1 && lastBrace !== -1 && lastBrace > firstBrace) {
    text = text.substring(firstBrace, lastBrace + 1);
  }

  // Attempt 1: Standard JSON parse
  try {
    return JSON.parse(text);
  } catch (e) {
    // Continue to repair
  }

  // Attempt 2: Sanitize control chars, trailing commas, duplicate closing brackets
  let cleaned = text
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, "")
    .replace(/,\s*([\]}])/g, '$1')
    .replace(/]\s*]/g, ']')
    .replace(/}\s*}/g, '}');

  try {
    return JSON.parse(cleaned);
  } catch (e) {
    // Continue to attempt 3
  }

  // Attempt 3: Stack-based repair for missing quotes/brackets or truncated structures
  let repaired = cleaned.replace(/:\s*([}\]])/g, ": null$1");

  const stack: string[] = [];
  let inString = false;
  let escape = false;
  let result = "";

  for (let i = 0; i < repaired.length; i++) {
    const char = repaired[i];
    result += char;
    if (escape) {
      escape = false;
      continue;
    }
    if (char === '\\') {
      escape = true;
      continue;
    }
    if (char === '"') {
      inString = !inString;
      continue;
    }
    if (!inString) {
      if (char === '{' || char === '[') stack.push(char === '{' ? '}' : ']');
      else if (char === '}' || char === ']') stack.pop();
    }
  }

  if (inString) result += '"';
  result = result.replace(/,\s*$/g, '');
  while (stack.length > 0) {
    result += stack.pop();
  }
  result = result.replace(/,\s*([\]}])/g, '$1');

  try {
    return JSON.parse(result);
  } catch (e) {
    console.warn('Gamification Engine: repairAndParseJSON failed to parse final result:', e);
    return {};
  }
}

export const evaluateGamificationState = async (force: boolean = false) => {
  if (!auth.currentUser) return;
  
  if (isEvaluating) {
    console.log('Gamification Engine: Evaluation already in progress, skipping.');
    return;
  }

  const COOLDOWN_MS = 60 * 1000; // 1-minute rate-limit/cooldown between evaluations
  if (Date.now() - lastEvaluationTime < COOLDOWN_MS && !force) {
    console.log('Gamification Engine: Skipping evaluation due to 1-minute cooldown/rate-limit.');
    return;
  }

  isEvaluating = true;
  const uid = auth.currentUser.uid;
  const db = getFirestore();
  const userRef = doc(db, 'users', uid);
  
  let unprocessedLogs: any[] = [];

  try {
    // 1. Fetch user document to get demographics and current gamification state (using cache if available)
    let userData: any = null;
    const cached = (window as any).__userDocCache;
    if (cached && cached.uid === uid && cached.data) {
      userData = cached.data;
    } else {
      let userSnap;
      try {
        userSnap = await getDoc(userRef);
      } catch (e: any) {
        if (e?.message?.includes('offline') || e?.code === 'unavailable') {
          console.warn('Gamification Engine: Client is offline, skipping evaluation.');
          return;
        }
        throw e;
      }
      
      if (!userSnap.exists()) return;
      userData = userSnap.data();
    }

    // Secondary cooldown check from database-backed state (if cache wasn't initialized yet)
    const dbLastEval = userData.gamification?.last_evaluation_timestamp || 0;
    if (Date.now() - dbLastEval < COOLDOWN_MS && !force) {
      console.log('Gamification Engine: Skipping evaluation due to database-backed cooldown.');
      lastEvaluationTime = dbLastEval; // Sync the local memory timer
      return;
    }

    // 2. Fetch unprocessed gamification logs
    const logsRef = collection(db, 'users', uid, 'gamification_logs');
    const logsQuery = query(logsRef, where('engine_processed', '==', false));
    let logsSnap;
    try {
      logsSnap = await getDocs(logsQuery);
    } catch (e: any) {
      if (e?.message?.includes('offline') || e?.code === 'unavailable') {
        console.warn('Gamification Engine: Client is offline, skipping logs fetch.');
        return;
      }
      throw e;
    }
    
    // Extract logs if available
    if (logsSnap && !logsSnap.empty) {
      unprocessedLogs = logsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }

    // Update evaluation timer before making the request to prevent double-logging from rapid operations
    lastEvaluationTime = Date.now();

    // 3. Assemble payload for LLM
    const evaluationPayload = {
      user_profile: {
        createdAt: userData.createdAt,
        language: userData.language || 'en_UK',
        theme: userData.theme || 'dark',
        vibe: userData.vibe || 'cyberpunk',
        goals: userData.goals || {},
        gamification_state: userData.gamification || {}
      },
      unprocessed_logs: unprocessedLogs
    };

    // 4. Demographic & Perk-Driven Gamification Calculation
    const currentGam = userData.gamification || {};
    const totalProcessedLogs = (currentGam.total_processed_logs || 0) + unprocessedLogs.length;
    
    // Calculate streak
    const currentStreak = currentGam.current_streak || 1;
    const isUA = (userData.language || 'en_UK').startsWith('ua') || (userData.language || '').toLowerCase().includes('ua');
    const userAge = userData.age || (userData.profile?.birth_date ? Math.floor((Date.now() - new Date(userData.profile.birth_date).getTime()) / (365.25 * 24 * 3600 * 1000)) : undefined);
    const userGender = userData.gender || userData.profile?.gender || 'male';

    // Calculate Demographic Cohort and Active Title
    const cohort = calculateDemographicCohort(userAge, userData.createdAt);
    const resolvedTitle = resolveDemographicTitle(cohort, userGender, totalProcessedLogs);

    const currentTier = String(resolvedTitle.tier);
    const titleName = isUA ? resolvedTitle.title.ua : resolvedTitle.title.en;
    const neonColor = resolvedTitle.neonColor;

    // Determine next title target
    let nextTitle = isUA ? "Максимальний Рівень" : "Max Rank";
    let completionPercentage = 100;

    if (resolvedTitle.tier === 1) {
      nextTitle = isUA ? "Майстер Макросу" : "Macro Manager";
      completionPercentage = Math.min(100, Math.round((totalProcessedLogs / 3) * 100));
    } else if (resolvedTitle.tier === 2) {
      nextTitle = isUA ? "Сигма Мезоморф" : "Sigma Shredder";
      completionPercentage = Math.min(100, Math.round(((totalProcessedLogs - 3) / 7) * 100));
    } else if (resolvedTitle.tier === 3) {
      nextTitle = isUA ? "Апекс Архітектор" : "Apex Architect";
      completionPercentage = Math.min(100, Math.round(((totalProcessedLogs - 10) / 15) * 100));
    }

    const triggeredNotifications: any[] = [];

    // Check Bio-Box Drops
    try {
      // 1. Daily Supply Bio-Box Check
      const dailyAwarded = await checkAndAwardDailyBox(uid);
      if (dailyAwarded) {
        triggeredNotifications.push({
          id: `box_daily_${Date.now()}`,
          type: 'BOX_DROP',
          message_localized: isUA ? 'Отримано Щоденний Біо-Бокс Припасів! 📦' : 'Daily Supply Bio-Box Ready! 📦',
          bro_vibe_flavor_text: isUA ? 'Твій щоденний хабар за перший прийом їжі активовано! ⚡' : 'Daily drop is primed, homie! Open it in the Vault ⚡'
        });
      }

      // 2. Streak Matrix Bio-Box Check
      const streakAwarded = await checkAndAwardStreakBox(uid, currentStreak);
      if (streakAwarded) {
        triggeredNotifications.push({
          id: `box_streak_${Date.now()}`,
          type: 'BOX_DROP',
          message_localized: isUA ? `Отримано Матричний Стрік-Бокс (${currentStreak} днів)! 🔥` : `Streak Matrix Bio-Box Unlocked (${currentStreak} Days)! 🔥`,
          bro_vibe_flavor_text: isUA ? `Твій стрік горить пекельним вогнем! Забирай нагороду 🔥` : `Your streak is straight fire! Matrix Bio-Box unlocked 🔥`
        });
      }

      // 3. Perfect Macro Bio-Box Vault Check
      if (userData.goals?.calories && userData.goals?.protein) {
        let todayCal = unprocessedLogs.reduce((acc, l) => acc + (l.calories_kcal || 0), 0);
        let todayPro = unprocessedLogs.reduce((acc, l) => acc + (l.protein_g || 0), 0);

        // Fallback to local history or userData.days for today's logs if gamification_logs subcollection is empty
        if (todayCal === 0 && typeof window !== 'undefined') {
          try {
            const todayStr = new Date().toISOString().split('T')[0];
            const storedHistory = localStorage.getItem('history') || localStorage.getItem('main_nutrition_history');
            if (storedHistory) {
              const entries = JSON.parse(storedHistory);
              const todayEntries = entries.filter((e: any) => {
                const d = e.date || (e.timestamp ? new Date(e.timestamp).toISOString().split('T')[0] : '');
                return d === todayStr;
              });
              todayCal = todayEntries.reduce((acc: number, e: any) => acc + (Number(e.calories) || Number(e.calories_kcal) || 0), 0);
              todayPro = todayEntries.reduce((acc: number, e: any) => acc + (Number(e.protein) || Number(e.protein_g) || 0), 0);
            }
          } catch (e) {}
        }
        if (todayCal === 0 && userData.days) {
          const todayStr = new Date().toISOString().split('T')[0];
          const todayData = userData.days[todayStr];
          if (todayData) {
            todayCal = Number(todayData.totalCalories) || Number(todayData.calories) || 0;
            todayPro = Number(todayData.totalProtein) || Number(todayData.protein) || 0;
          }
        }

        if (todayCal > 0) {
          const macroAwarded = await checkAndAwardPerfectMacroBox(
            uid,
            userData.goals.calories,
            todayCal,
            userData.goals.protein,
            todayPro
          );
          if (macroAwarded) {
            triggeredNotifications.push({
              id: `box_macro_${Date.now()}`,
              type: 'BOX_DROP',
              message_localized: isUA ? 'Отримано Сейф Ідеального Макросу! 👑' : 'Perfect Macro Bio-Box Vault Unlocked! 👑',
              bro_vibe_flavor_text: isUA ? 'Ідеальне попадання в БЖВ! Абсолютний Apex рівень 👑' : 'Hit macros dead-on! Elite cyber-vault unlocked 👑'
            });
          }
        }
      }
    } catch (boxErr) {
      console.warn('Gamification Engine: Bio-Box evaluation error:', boxErr);
    }

    // Calorie Streak Rule check
    // "Ayo, homie! Your streak is fire 🔥" MUST ONLY be displayed on days 3, 7, 14, 21, and 28.
    const allowedStreakDays = [3, 7, 14, 21, 28];
    if (allowedStreakDays.includes(currentStreak)) {
      const streakNotifKey = `streak_notif_day_${currentStreak}`;
      if (!userData.notifications_sent?.[streakNotifKey]) {
        triggeredNotifications.push({
          id: `streak_${Date.now()}`,
          type: 'STREAK',
          message_localized: isUA ? `Стрік досягнуто!` : `Streak Milestone!`,
          bro_vibe_flavor_text: "Ayo, homie! Your streak is fire 🔥"
        });
      }
    }

    // Level / Tier Unlocked notification
    if (currentTier !== (currentGam.current_tier || "1")) {
      triggeredNotifications.push({
        id: `lvl_${Date.now()}`,
        type: 'LEVEL_UP',
        message_localized: isUA ? `Новий статус: ${titleName}!` : `New Rank: ${titleName}!`,
        bro_vibe_flavor_text: isUA ? `Вітаємо, ти підвищив свій рівень до ${titleName}! 🔥` : `Congrats homie, you hit ${titleName}! 🔥`
      });
    }

    const parsedData = {
      user_title: {
        current_tier: currentTier,
        title_name: titleName,
        neon_color_hex: neonColor,
        title_id: resolvedTitle.id,
        cohort: cohort,
        perks: resolvedTitle.perks
      },
      milestone_progress: {
        next_title: nextTitle,
        completion_percentage: completionPercentage,
        missing_requirements: []
      },
      triggered_notifications: triggeredNotifications,
      administrative_flags: []
    };

    // 5. Batch write the updates to Firestore
    const batch = writeBatch(db);

    const notificationsSentUpdate: any = {};
    if (allowedStreakDays.includes(currentStreak)) {
      const streakNotifKey = `streak_notif_day_${currentStreak}`;
      if (!userData.notifications_sent?.[streakNotifKey]) {
        notificationsSentUpdate[`notifications_sent.${streakNotifKey}`] = true;
      }
    }

    // Update user document
    batch.update(userRef, {
      ...notificationsSentUpdate,
      gamification: {
        current_tier: parsedData.user_title.current_tier,
        title_name: parsedData.user_title.title_name,
        neon_color_hex: parsedData.user_title.neon_color_hex,
        next_title: parsedData.milestone_progress.next_title,
        completion_percentage: parsedData.milestone_progress.completion_percentage,
        missing_requirements: parsedData.milestone_progress.missing_requirements,
        total_processed_logs: totalProcessedLogs,
        last_evaluation_timestamp: Date.now(),
        mutual_likes_count: currentGam.mutual_likes_count || 0
      }
    });

    // Mark logs as processed
    unprocessedLogs.forEach(log => {
      const logRef = doc(db, 'users', uid, 'gamification_logs', log.id);
      batch.update(logRef, { engine_processed: true });
    });

    // Write new notifications
    if (parsedData.triggered_notifications && Array.isArray(parsedData.triggered_notifications)) {
      parsedData.triggered_notifications.forEach((notif: any) => {
        const notifId = notif.id || Math.random().toString(36).substring(2, 15);
        const newNotifRef = doc(db, 'users', uid, 'notifications', notifId);
        batch.set(newNotifRef, {
          id: notifId,
          timestamp: Date.now(),
          type: notif.type || 'LEVEL_UP',
          title: notif.message_localized || 'Achievement Unlocked!',
          message: notif.bro_vibe_flavor_text || '',
          read: false
        });
      });
    }

    await batch.commit();
    console.log('Gamification Engine evaluation complete!');

  } catch (error) {
    console.error('Error evaluating gamification state:', error);
    if (unprocessedLogs.length > 0) {
      try {
        const fallbackBatch = writeBatch(db);
        unprocessedLogs.forEach(log => {
          const logRef = doc(db, 'users', uid, 'gamification_logs', log.id);
          fallbackBatch.update(logRef, { engine_processed: true, engine_error: true });
        });
        await fallbackBatch.commit();
        console.log(`Marked ${unprocessedLogs.length} logs as processed during error recovery.`);
      } catch (fallbackError) {
        console.error("Failed to commit fallback gamification batch:", fallbackError);
      }
    }
  } finally {
    isEvaluating = false;
  }
};
